import { useAdCampaignDetail } from 'data/ad-campaign';
import React from 'react';
import { EAdCampaignStatus } from 'types/ad-campaign';

import { Box, ButtonBase, useTheme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import LibraryAddCheckRoundedIcon from '@material-ui/icons/LibraryAddCheckRounded';

import Button from 'creative-components/CustomButtons/Button';
import GridContainer from 'creative-components/Grid/GridContainer';
import GridItem from 'creative-components/Grid/GridItem';

import ActionModal from 'components/ActionModal/ActionModal';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';
import PaymentMethodSelector from 'components/PaymentMethods/PaymentMethodSelector';

import { updateAdCampaign } from 'utils/api/ad-campaigns';
import { showAPIErrorAlert } from 'utils/lib';
import { toast } from 'sonner';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    fontWeight: 400,
  },
  settingContainer: {
    backgroundColor: '#F5F5F5',
    borderRadius: '12px',
    padding: '20px',
    margin: '0 0 30px',

    '& > h4': {
      fontSize: '16px',
      fontWeight: 600,
      color: theme.palette.darkGray.main,
    },
    '& > h5': {
      fontSize: '14px',
      color: theme.palette.gray.main,
      fontWeight: 500,
      margin: '6px 0 10px',
    },
  },
  settingInput: {
    maxWidth: '400px',
    fontWeight: 500,
  },
  cancelButton: {
    backgroundColor: `${theme.palette.grayScale6.main} !important`,
  },
}));

type Props = {
  adCampaignId: string;
};

export default ({ adCampaignId }: Props) => {
  const theme = useTheme();
  const classes = useStyles();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext() as any; // TODO: TEMP AS ANY

  const { adCampaign, isLoadingAdCampaign, mutateAdCampaign } = useAdCampaignDetail(adCampaignId);

  const [showConfirmation, setShowConfirmation] = React.useState<React.ReactNode | null>(null);

  const [paymentMethodId, setPaymentMethodId] = React.useState(adCampaign.paymentMethodId ?? '');

  const onUpdatePaymentMethodId = async (newPaymentMethodId: string) => {
    showLoadingIndicatorModal();
    try {
      await updateAdCampaign(adCampaign._id, { paymentMethodId: newPaymentMethodId });

      mutateAdCampaign();

      toast.success('Your payment method has been updated.');
    } catch (err) {
      console.error(err);
      showAPIErrorAlert(err);
    } finally {
      hideLoadingIndicatorModal();
    }
  };

  const onConfirmedPauseOrResume = async () => {
    const newStatus = [EAdCampaignStatus.PendingMarketingProfile, EAdCampaignStatus.Active].includes(adCampaign.status)
      ? EAdCampaignStatus.Paused
      : EAdCampaignStatus.Active;

    showLoadingIndicatorModal();
    try {
      await updateAdCampaign(adCampaign._id, { status: newStatus });

      mutateAdCampaign();

      if (newStatus === EAdCampaignStatus.Active) {
        toast.success('Your digital ad has been resumed!');
      } else {
        toast.success('Your digital ad has been paused. You may resume it at any time to continue showing it to your audience!');
      }
    } catch (err) {
      console.error(err);
      showAPIErrorAlert(err);
    } finally {
      hideLoadingIndicatorModal();
      setShowConfirmation(null);
    }
  };

  const onPauseOrResume = async () => {
    setShowConfirmation(<ActionModal
      backgroundColor={theme.palette.offWhite.main}
      textColor={theme.palette.darkGray.main}
      icon={<LibraryAddCheckRoundedIcon />}
      onClose={() => setShowConfirmation(null)}
      title={[EAdCampaignStatus.PendingMarketingProfile, EAdCampaignStatus.Active].includes(adCampaign.status) ? 'Pause My Ad' : 'Resume My Ad'}
      message={
        [EAdCampaignStatus.PendingMarketingProfile, EAdCampaignStatus.Active].includes(adCampaign.status)
          ? 'Are you sure you want to pause your ad? Your ad will run until it uses up its current monthly budget and then will stop showing to your audience.'
          : 'Your monthly subscription will restart today and your ad will start showing to your audience again.'
        }
      buttons={[
        <ButtonBase onClick={() => setShowConfirmation(null)} className={classes.cancelButton}>Cancel</ButtonBase>,
        <ButtonBase onClick={() => onConfirmedPauseOrResume()}>
          {[EAdCampaignStatus.PendingMarketingProfile, EAdCampaignStatus.Active].includes(adCampaign.status) ? 'Pause Ad' : 'Resume Ad'}
        </ButtonBase>,
      ]}
    />);
  };

  if (isLoadingAdCampaign) {
    return (
      <div className={classes.root}>
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {showConfirmation}

      <GridContainer justifyContent="flex-start">
        <GridItem xs={12}>
          <div className={classes.settingContainer}>
            <h4>General</h4>
            <h5>Manage your basic preferences and default configurations.</h5>

            <Box style={{ height: '30px' }} />

            <PaymentMethodSelector
              inputProps={{
                value: paymentMethodId,
                onChange: (event: any) => {
                  const newPaymentMethodId = event.target.value;
                  setPaymentMethodId(newPaymentMethodId);
                  onUpdatePaymentMethodId(newPaymentMethodId);
                },
                className: classes.settingInput,
              }}
            />

            <Box style={{ height: '30px' }} />

            <Button
              style={{ marginTop: '130px' }}
              round
              color="primary"
              onClick={() => onPauseOrResume()}
            >
              {[EAdCampaignStatus.PendingMarketingProfile, EAdCampaignStatus.Active].includes(adCampaign.status) ? 'Pause Ad' : 'Resume Ad'}
            </Button>
          </div>
        </GridItem>
      </GridContainer>
    </div>
  );
};
